import type {
    GridFilterItem,
    GridDataSource as GridDataSourcePremium,
    GridGetRowsParams as GridGetRowsParamsPremium,
    GridGetRowsResponse as GridGetRowsResponsePremium,
} from '@mui/x-data-grid-premium';
import type { GridAggregationModel } from '@mui/x-data-grid-premium';
import { executeForIds, executeQueryPBF } from '@arcgis/core/rest/query.js';
import { bufferCount, from, lastValueFrom, map, mergeMap, reduce } from 'rxjs';
import type Graphic from '@arcgis/core/Graphic';
import moment from 'moment-timezone';
import esriRequest from '@arcgis/core/request.js';
import { v4 as uuidv4 } from 'uuid';
import type { Dispatch, SetStateAction } from 'react';
interface FieldMeta {
    [key: string]: __esri.Field;
}

const operator_map: {
    [key: string]: { operator: string; format?: (value: any) => string | string[] };
} = {
    contains: { operator: 'LIKE', format: (value: any) => `'%${value}%'` },
    doesNotContain: { operator: 'NOT LIKE', format: (value: any) => `'%${value}%'` },
    isEmpty: { operator: 'IS NULL' },
    isNotEmpty: { operator: 'IS NOT NULL' },
    equals: { operator: '=', format: (value: any) => `'${value}'` },
    notEqual: { operator: '<>', format: (value: any) => `'${value}'` },
    startsWith: { operator: 'LIKE', format: (value: any) => `'${value}%'` },
    endsWith: { operator: 'LIKE', format: (value: any) => `'%${value}'` },
    //?
    is: {
        operator: '=',
        format: (value: any) => {
            if (typeof value === 'string' && value.toLowerCase() === 'null') {
                return `'${value}'`;
            } else if (Array.isArray(value) && value.find((v) => v.toLowerCase() === 'null')) {
                return `(${value.map((v) => `'${v.trim()}'`).join(',')}) OR ${value.find((v) => v.toLowerCase() === 'null') ? 'field IS NULL' : ''}`;
            } else if (value instanceof Date) {
                return `'${moment(value).format('YYYY-MM-DD')}'`;
            }
            return `'${value}'`;
        },
    },

    isAnyOf: {
        operator: 'IN',
        format: (value: any) => `(${(value as string[]).map((v) => `'${v.trim()}'`).join(',')})`,
    },
    // dates?
    after: { operator: '>', format: (value: any) => value },
    onOrAfter: { operator: '>=', format: (value: any) => value },
    before: { operator: '<', format: (value: any) => value },
    onOrBefore: { operator: '<=', format: (value: any) => value },
    // numbers
    '=': { operator: '=', format: (value: any) => value },
    '!=': { operator: '<>', format: (value: any) => value },
    '<': { operator: '<', format: (value: any) => value },
    '<=': { operator: '<=', format: (value: any) => value },
    '>': { operator: '>', format: (value: any) => value },
    '>=': { operator: '>=', format: (value: any) => value },
};

function format_field(f: GridFilterItem, field: __esri.Field) {
    const o = operator_map[f.operator];

    // If we don't recognize the operator, return a no-op clause.
    if (!o) {
        return '1=1';
    }

    switch (field.type) {
        case 'string':
            if (o.format) {
                return `${f.field} ${o.operator} ${o.format(f.value)}`;
            } else {
                return `${f.field} ${o.operator}`;
            }

        // Numeric field types
        case 'small-integer':
        case 'integer':
        case 'single':
        case 'double':
        case 'oid': {
            const rawValue = o.format ? o.format(f.value) : f.value;
            const numValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);

            if (Number.isNaN(numValue)) {
                // Invalid numeric value; return a no-op clause.
                return '1=1';
            }

            return `${f.field} ${o.operator} ${numValue}`;
        }

        // Date fields: use milliseconds since epoch (ArcGIS-compatible)
        case 'date': {
            const rawValue = o.format ? o.format(f.value) : f.value;
            const m = moment(rawValue);
            const ms = m.valueOf();

            if (!m.isValid() || Number.isNaN(ms)) {
                // Invalid date; return a no-op clause.
                return '1=1';
            }

            return `${f.field} ${o.operator} ${ms}`;
        }

        // Fallback for any other field types: treat value as-is
        default: {
            const valuePart = o.format ? o.format(f.value) : f.value;
            return `${f.field} ${o.operator} ${valuePart}`;
        }
    }
}

// function capture_fi(fields: __esri.Field[]): string[] {
//   const date_fields: string[] = [];
//   fields.forEach(f => {
//     if (f.type === 'date') {
//       date_fields.push(f.name);
//     }
//   });
//   return date_fields;
// }

// capture field info and use it to determine how to format queries

function convert_dates(feature: Graphic, fields: FieldMeta) {
    Object.keys(fields)
        .filter((k) => fields[k].type === 'date')
        .forEach((df) => {
            if (feature.attributes[df]) {
                feature.attributes[df] = new Date(feature.attributes[df]);
            }
        });
    return feature;
}





/**
 * Computes an aggregate row from the given rows based on the aggregation model.
 * Each field in the aggregation model maps to an aggregation function name (e.g. 'sum').
 * The returned object contains the computed aggregate value for each field.
 */
function computeAggregateRow(
    rows: Record<string, any>[],
    aggregationModel: GridAggregationModel
): Record<string, any> {
    const aggregateRow: Record<string, any> = {};

    for (const [field, functionName] of Object.entries(aggregationModel)) {
        switch (functionName) {
            case 'sum':
                aggregateRow[field] = rows.reduce((acc, row) => {
                    const value = row[field];
                    return acc + (typeof value === 'number' ? value : 0);
                }, 0);
                break;

            case 'avg': {
                const numericValues = rows
                    .map((row) => row[field])
                    .filter((v): v is number => typeof v === 'number');
                aggregateRow[field] =
                    numericValues.length > 0
                        ? numericValues.reduce((a, b) => a + b, 0) / numericValues.length
                        : 0;
                break;
            }

            case 'min':
                aggregateRow[field] = rows.reduce((min, row) => {
                    const value = row[field];
                    if (typeof value !== 'number') return min;
                    return min === null ? value : Math.min(min, value);
                }, null as number | null);
                break;

            case 'max':
                aggregateRow[field] = rows.reduce((max, row) => {
                    const value = row[field];
                    if (typeof value !== 'number') return max;
                    return max === null ? value : Math.max(max, value);
                }, null as number | null);
                break;

            case 'size':
                aggregateRow[field] = rows.length;
                break;

            default:
                // Unknown aggregation function — skip
                break;
        }
    }

    return aggregateRow;
}


export function featureServiceGridDataSource(
    url: string,
    baseWhere = "",
    uidField = "",
    unsavedChangesRef?: { current: { unsavedRows: Record<string, any> } }
): GridDataSourcePremium {
    let lastQuery = '';
    let lastOrderBy = '';
    let features: Graphic[] = [];
    let ids: (number | string)[] = [];
    let field_meta: FieldMeta;

    return {
        getRows: async (
            params: GridGetRowsParamsPremium
        ): Promise<GridGetRowsResponsePremium> => {

            // capture field meta first in case we have not run a query yet
            if (!field_meta) {
                const r = (await esriRequest(url, { query: { f: 'json' } }).then(
                    (r) => r.data
                )) as any;
                field_meta = r.fields.reduce((a: FieldMeta, c: __esri.Field) => {
                    a[c.name] = c;
                    return a;
                }, {} as FieldMeta);
            }
            // todo: if pagination is configured we should use it to limit the number of features fetched
            const where = generateWhereFromParams(params, field_meta, baseWhere);

            // add base where
            const orderByFields =
                params.sortModel?.map((sort) => `${sort.field} ${sort.sort}`) ?? [];
            if (lastQuery !== where || lastOrderBy !== orderByFields.join(',')) {
                const q = {
                    where,
                    orderByFields,
                };

                lastQuery = q.where;
                lastOrderBy = orderByFields.join(',');

                ids = await executeForIds(url, q);

                features = await fetchFeatures(params, ids, url, field_meta);
            }

            if (params.sortModel?.length) {
                const sort = params.sortModel[0];
                const s = sort.sort === 'asc' ? 1 : -1;
                features.sort((a, b) => {
                    const aVal = a.attributes[sort.field];
                    const bVal = b.attributes[sort.field];

                    // Handle nulls — push them to the end
                    if (aVal == null && bVal == null) return 0;
                    if (aVal == null) return 1;
                    if (bVal == null) return -1;

                    // Numeric comparison
                    if (typeof aVal === 'number' && typeof bVal === 'number') {
                        return (aVal - bVal) * s;
                    }

                    // String comparison
                    return (
                        String(aVal).localeCompare(String(bVal), undefined, {
                            sensitivity: 'base',
                        }) * s
                    );
                });
            }

            const rows = features.map((a) => {
                    const id = uidField ? a.attributes[uidField] : uuidv4();
                    return { rowId: id, ...a.attributes };
                });

            // Merge unsaved local edits on top of fetched rows so both the
            // displayed rows and the aggregation footer reflect pending changes.
            const mergedRows = unsavedChangesRef?.current
                ? rows.map((row) => {
                      const unsaved = unsavedChangesRef.current.unsavedRows[row.rowId];
                      return unsaved ? { ...row, ...unsaved } : row;
                  })
                : rows;

            const aggregateRow = params.aggregationModel
                ? computeAggregateRow(mergedRows, params.aggregationModel)
                : undefined;

            return {
                rows: mergedRows,
                rowCount: ids.length,
                aggregateRow,
            };
        },

        getAggregatedValue(row, field) {
            return row[field] ?? null;
        },
    };
}

async function fetchFeatures(params, ids, url, field_meta): Promise<Graphic[]> {
    return await lastValueFrom(
        from(ids).pipe(
            bufferCount(1000),
            mergeMap(
                (objectIds) =>
                    from(
                        executeQueryPBF(url, {
                            objectIds, // simplistic where clause for demo purposes
                            outFields: ['*'],
                            orderByFields: [
                                ...(params.sortModel?.map((sort) => `${sort.field} ${sort.sort}`) ??
                                    []),
                            ],
                        })
                    ),
                5
            ),
            map((r) => r.features.map((f) => convert_dates(f, field_meta))),
            reduce((a, r) => a.concat(r))
        )
    );
}

function generateWhereFromParams(
    params: GridGetRowsParamsPremium,
    field_meta: FieldMeta,
    baseWhere?: string
): string {
    // grid filters
    let where = params.filterModel.items
        .filter((fm) => !fm.field.startsWith('_'))
        .map((f) => format_field(f, field_meta))
        .join(` ${params.filterModel.logicOperator} `);

    // use filter items prefixed with _ as raw where clause expressions (e.g. _internalWhereClause)
    const internalExpressions = params.filterModel.items
        .filter((fm) => fm.field.startsWith('_') && fm.value)
        .map((fm) => fm.value as string);

    for (const expr of internalExpressions) {
        where = where ? `(${where}) AND (${expr})` : expr;
    }

    // base where clause from configuration
    if (baseWhere) {
        where = where ? `(${where}) AND (${baseWhere})` : baseWhere;
    }
    return where;
}